import { createRouter, createWebHistory } from 'vue-router';
import { useI18n } from 'vue-i18n';

// Supported languages
export const supportedLanguages = ['en', 'pl', 'fr', 'de'];

// Lazy-loaded views for better performance
const Home = () => import('../views/HomeSimple.vue');
const Login = () => import('../views/Login.vue');
const Register = () => import('../views/Register.vue');
const VoiceModel = () => import('../views/VoiceModel.vue');
const CreateCover = () => import('../views/CreateCover.vue');
const MyCoversList = () => import('../views/MyCoversList.vue');

// Create routes for all languages
let routes = [];

// Add language-specific routes
supportedLanguages.forEach(lang => {
  routes = routes.concat([
    {
      path: `/${lang}`,
      name: `${lang}-home`,
      component: Home,
      meta: { requiresAuth: false, lang }
    },
    {
      path: `/${lang}/login`,
      name: `${lang}-login`,
      component: Login,
      meta: { requiresAuth: false, lang }
    },
    {
      path: `/${lang}/register`,
      name: `${lang}-register`,
      component: Register,
      meta: { requiresAuth: false, lang }
    },
    {
      path: `/${lang}/voice-model`,
      name: `${lang}-voice-model`,
      component: VoiceModel,
      meta: { requiresAuth: true, lang }
    },
    {
      path: `/${lang}/create-cover`,
      name: `${lang}-create-cover`,
      component: CreateCover,
      meta: { requiresAuth: true, lang }
    },
    {
      path: `/${lang}/my-covers`,
      name: `${lang}-my-covers`,
      component: MyCoversList,
      meta: { requiresAuth: true, lang }
    }
  ]);
});

// Add root redirect - detect preferred language or default to English
routes.push({
  path: '/',
  redirect: to => {
    // Try to get language from localStorage
    let preferredLang = 'en';
    try {
      if (typeof localStorage !== 'undefined') {
        const storedLang = localStorage.getItem('user-language');
        if (storedLang && supportedLanguages.includes(storedLang)) {
          preferredLang = storedLang;
        }
      }
    } catch (e) {
      console.error('Error accessing localStorage:', e);
    }
    
    return `/${preferredLang}`;
  }
});

// Add legacy routes (redirect to route with language)
routes.push(
  {
    path: '/login',
    redirect: to => {
      const i18n = useI18n();
      return `/${i18n.locale.value}/login`;
    }
  },
  {
    path: '/register',
    redirect: to => {
      const i18n = useI18n();
      return `/${i18n.locale.value}/register`;
    }
  },
  {
    path: '/voice-model',
    redirect: to => {
      const i18n = useI18n();
      return `/${i18n.locale.value}/voice-model`;
    }
  },
  {
    path: '/create-cover',
    redirect: to => {
      const i18n = useI18n();
      return `/${i18n.locale.value}/create-cover`;
    }
  },
  {
    path: '/my-covers',
    redirect: to => {
      const i18n = useI18n();
      return `/${i18n.locale.value}/my-covers`;
    }
  }
);

// Catch-all route - redirect to home page with preferred language
routes.push({
  path: '/:pathMatch(.*)*',
  redirect: to => {
    let preferredLang = 'en';
    try {
      if (typeof localStorage !== 'undefined') {
        const storedLang = localStorage.getItem('user-language');
        if (storedLang && supportedLanguages.includes(storedLang)) {
          preferredLang = storedLang;
        }
      }
    } catch (e) {
      console.error('Error accessing localStorage:', e);
    }
    
    return `/${preferredLang}`;
  }
});

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes
});

// Import i18n instance directly
import { i18n } from '../i18n';

// Navigation guard to handle language and authentication
router.beforeEach((to, from, next) => {
  // Get language from route meta
  const routeLang = to.meta.lang;
  
  // Update document language if available
  if (routeLang && typeof document !== 'undefined') {
    try {
      document.documentElement.lang = routeLang;
      
      // Also update localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('user-language', routeLang);
      }
      
      // Update i18n locale - safer way
      if (i18n && i18n.global) {
        i18n.global.locale.value = routeLang;
      }
    } catch (e) {
      console.error('Error in language handling:', e);
    }
  }
  
  // For now, skip authentication checks to make sure routing works
  next();
});

export default router;
